import { getHostname } from './utils';
const PRIVATE_TOOLS_REGEXP = /^(tools|private)(?:-([a-z]+[0-9]))?.hubteam(qa)?\.com(?!\/foundations-editor)/;
export const THEME_DATA_TAG = 'hubspotTheme';
export const LOCAL_STORAGE_THEME_KEY = 'trellis:theme';
export const LOCAL_STORAGE_THEME_VERSION_KEY = 'trellis:theme-version';
export const LOCAL_STORAGE_THEME_OVERRIDES_KEY = 'trellis:theme-overrides';
export function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (_unused) {
    return undefined;
  }
}
function shouldReadLocalThemeSettings() {
  try {
    const hostname = getHostname();
    const isStorybook = window.HS_STORYBOOK !== undefined;
    // private/tools subdomain pages should not read theme from localStorage since not tokenized
    // storybook is an exception since we allow previewing components in different themes.
    if (hostname.match(PRIVATE_TOOLS_REGEXP) && !isStorybook) {
      return false;
    }
  } catch (_unused2) {
    // do nothing
  }
  return true;
}
function getThemeLocalStorageSetting(key) {
  if (!shouldReadLocalThemeSettings()) {
    return undefined;
  }
  return safeLocalStorageGet(key);
}
function splitChosenTheme(theme) {
  const [themeName, themeMode] = theme.split('-');
  if (!themeName || !themeMode) {
    return undefined;
  }
  return {
    themeName,
    themeMode
  };
}
function validateThemeVersion(themeVersion) {
  if (typeof themeVersion !== 'object' || !themeVersion.type) {
    return false;
  }
  if (themeVersion.type === 'deployed') {
    return typeof themeVersion.channel === 'string' && (!themeVersion.env || typeof themeVersion.env === 'string');
  }
  if (themeVersion.type === 'version') {
    return typeof themeVersion.version === 'string' && typeof themeVersion.channel === 'string';
  }
  if (themeVersion.type === 'local') {
    return typeof themeVersion.channel === 'string';
  }
  if (themeVersion.type === 'builtin') {
    return true;
  }
  return false;
}
function getThemeInfoFromHtml() {
  try {
    // dataset converts kebab to camel case
    const dataset = document.documentElement.dataset;
    if (!dataset) {
      return undefined;
    }
    const themeSetting = dataset[THEME_DATA_TAG];
    if (!themeSetting) {
      return undefined;
    }
    return splitChosenTheme(themeSetting);
  } catch (_unused3) {
    return undefined;
  }
}
function getChosenTheme() {
  const localThemeName = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_KEY);
  if (localThemeName) {
    return splitChosenTheme(localThemeName);
  }
  const themeInfoFromHtml = getThemeInfoFromHtml();
  if (themeInfoFromHtml) {
    return themeInfoFromHtml;
  }
  return undefined;
}
function getThemeVersion() {
  const localThemeVersion = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_VERSION_KEY);
  if (localThemeVersion) {
    try {
      const themeVersion = JSON.parse(localThemeVersion);
      if (validateThemeVersion(themeVersion)) {
        return themeVersion;
      }
    } catch (_unused4) {
      // do nothing
    }
  }
  return {
    type: 'builtin'
  };
}
function getThemeOverrides() {
  const localThemeSettings = getThemeLocalStorageSetting(LOCAL_STORAGE_THEME_OVERRIDES_KEY);
  if (localThemeSettings) {
    try {
      const themeOverrides = JSON.parse(localThemeSettings);
      if (typeof themeOverrides === 'object') {
        return themeOverrides;
      }
    } catch (_unused5) {
      // do nothing
    }
  }
  return {};
}
export function getLocalThemeSettings() {
  const chosenTheme = getChosenTheme();
  const themeVersion = getThemeVersion();
  const themeOverrides = getThemeOverrides();
  if (!chosenTheme) {
    return {
      themeName: undefined,
      themeMode: undefined,
      themeVersion,
      themeOverrides
    };
  }
  return Object.assign({
    themeOverrides,
    themeVersion
  }, chosenTheme);
}